import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import Avatar from 'components/base/Avatar';
import RevealDropdown from 'components/base/RevealDropdown';
import { Dropdown, Modal, Button, Form } from 'react-bootstrap';
import { useState, ChangeEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  deleteHVAService,
  updateHVAService
} from 'services/hvaservicecampaign.service';

type HVAService = {
  id: number;
  name: string;
  brand: string;
  description: string;
};

type ServiceActionCellProps = {
  id: number;
  name: string;
  brand: string;
  description: string;
  onUpdate: () => void;
};

const ServiceActionCell = ({
  id,
  name,
  brand,
  description,
  onUpdate
}: ServiceActionCellProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    description: ''
  });

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCloseDeleteModal = () => setShowDeleteModal(false);
  const handleShowDeleteModal = (id: number) => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setFormData({ name: '', brand: '', description: '' });
  };

  const handleShowEditModal = (id: number) => {
    setSelectedId(id);
    setFormData({
      name,
      brand,
      description
    });
    setShowEditModal(true);
  };

  const handleConfirmRemove = async () => {
    if (selectedId !== null) {
      try {
        await deleteHVAService(selectedId);
        console.log(`Service with ID: ${selectedId} deleted successfully`);
        onUpdate(); // Actualizamos la tabla después de eliminar
        handleCloseDeleteModal();
      } catch (error) {
        console.error(`Error deleting Service with ID: ${selectedId}`, error);
      }
    }
  };

  const handleUpdateService = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedId !== null) {
      try {
        await updateHVAService(selectedId, formData);
        // console.log(`Service with ID: ${selectedId} updated successfully`);
        onUpdate(); // Actualizamos la tabla después de editar
        handleCloseEditModal();
      } catch (error) {
        console.error(`Error updating Service with ID: ${selectedId}`, error);
      }
    }
  };

  return (
    <>
      <RevealDropdown btnClassName="p-2 pt-1 pb-1 fs-11">
        <Dropdown.Item onClick={() => handleShowEditModal(id)}>
          <FontAwesomeIcon icon={faUserPen} className="fs-8 p-1 pt-0 pb-0" />
          Edit
        </Dropdown.Item>
        <Dropdown.Item
          onClick={() => handleShowDeleteModal(id)}
          className="text-danger"
        >
          <FontAwesomeIcon icon={faTrash} className="fs-8 p-1 pt-0 pb-0" />
          Remove
        </Dropdown.Item>
      </RevealDropdown>

      {/* Modal de Eliminación */}
      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this Service?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmRemove}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Edición */}
      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Form onSubmit={handleUpdateService}>
          <Modal.Header closeButton>
            <Modal.Title>Update Service with ID: {id} </Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Brand</Form.Label>
              <Form.Control
                type="text"
                name="brand"
                value={formData.brand}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                type="text"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update Service
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export const hvaservicessTablecolumns = (
  onUpdate: () => void
): ColumnDef<HVAService>[] => [
  {
    accessorKey: 'name',
    header: 'Service',
    cell: ({ row: { original } }) => {
      const { name, brand } = original;
      const [imageSrc, setImageSrc] = useState(`/brands/${brand}.png`);

      const handleImageError = () => {
        setImageSrc('/brands/default.png');
      };

      return (
        <div className="d-flex align-items-center text-body-emphasis">
          <Avatar
            src={imageSrc}
            size="m"
            alt={name}
            onError={handleImageError}
          />
          &nbsp;{name}
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: '15%' }, className: 'pe-2 ps-0' },
      cellProps: { className: 'p-0 fw-bold white-space-nowrap' }
    }
  },
  {
    accessorKey: 'action',
    enableSorting: false,
    header: '',
    cell: ({ row: { original } }) => {
      const { id, name, brand, description } = original;
      return (
        <ServiceActionCell
          id={id}
          name={name}
          brand={brand}
          description={description}
          onUpdate={onUpdate}
        />
      );
    },
    meta: {
      cellProps: { className: 'text-body-tertiary' }
    }
  },
  {
    accessorKey: 'id',
    header: 'ID',
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1' },
      cellProps: { className: 'fw-bold px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'brand',
    header: 'brand logo',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1' },
      cellProps: { className: 'fw-bold px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'description',
    header: 'Description',
    meta: {
      headerProps: { style: { width: '70%' }, className: 'px-1' },
      cellProps: { className: 'text-body-tertiary px-1' }
    }
  }
];

export const HVAServicesTable = () => {
  return (
    <div>
      <AdvanceTable
        tableProps={{ className: 'phoenix-table fs-9' }}
        rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
      />
      <AdvanceTableFooter pagination />
    </div>
  );
};
